import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { 
  Calendar, Clock, Dumbbell, Play, CheckCircle, Lock, Flame,
  ChevronRight, Trophy, Target, Zap, Heart, Moon, Star, Video,
  Pause, SkipForward, Volume2, VolumeX, RotateCcw, Timer,
  TrendingUp, Award, Users, Sparkles, Crown, Shield, ArrowLeftRight,
  Undo2, Redo2, Droplets, Coffee, Settings, Minus, Plus, Share2, Download, X, Copy,
  AlertCircle
} from "lucide-react";
import ExerciseVideo from "@/components/ExerciseVideo";
import ExerciseSwapper from "@/components/ExerciseSwapper";
import ActionControls from "@/components/ActionControls";
import DrillAnalyzer, { FloatingAnalyzeButton } from "@/components/DrillAnalyzer";
import QuoteShareModal from "@/components/QuoteShareModal";
import GrindBeastMode from "@/components/grind/GrindBeastMode";
import GrindDayTracker from "@/components/grind/GrindDayTracker";
import {
  BETA_MODE, PHASES, SKILL_LEVELS, MOTIVATIONAL_QUOTES,
  DAILY_CHALLENGES, ELITE_BEAST_CHALLENGES, TEAM_CHALLENGES,
  getWeeklyEliteChallenge,
} from "@/components/grind/GrindConstants";

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.08 } }
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper to fetch video
const fetchExerciseVideo = async (drillId) => {
  try {
    const token = localStorage.getItem('token');
    const response = await axios.get(`${API}/videos/exercise/${drillId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data.video;
  } catch (e) {
    console.error('Error loading video:', e);
    return null;
  }
};

// ==================== WORKOUT TIMER WITH AUDIO CUES & MUSIC ====================
const WorkoutTimer = ({ drill, sets = 3, onComplete, onClose }) => {
  const [currentSet, setCurrentSet] = useState(1);
  const [timeLeft, setTimeLeft] = useState(drill.duration_seconds);
  const [isRunning, setIsRunning] = useState(false);
  const [isResting, setIsResting] = useState(false);
  const [audioEnabled, setAudioEnabled] = useState(true);
  const [completed, setCompleted] = useState(false);
  
  // Music state
  const [musicEnabled, setMusicEnabled] = useState(true);
  const [musicVolume, setMusicVolume] = useState(0.4);
  const [currentBPM, setCurrentBPM] = useState(140); // Work BPM
  
  // Water break state
  const [isWaterBreak, setIsWaterBreak] = useState(false);
  const [waterBreakTimeLeft, setWaterBreakTimeLeft] = useState(30);
  const [totalWaterConsumed, setTotalWaterConsumed] = useState(0);
  const [timeSinceLastWater, setTimeSinceLastWater] = useState(0);
  const [showWaterReminder, setShowWaterReminder] = useState(false);
  
  // Rest settings
  const [customRestTime, setCustomRestTime] = useState(drill.rest_seconds || 60);
  const [showRestSettings, setShowRestSettings] = useState(false);
  
  const intervalRef = useRef(null);
  const audioContextRef = useRef(null);
  const speechSynthRef = useRef(null);
  const musicIntervalRef = useRef(null);
  const beatGainRef = useRef(null);
  const waterReminderRef = useRef(null);

  // BPM settings
  const WORK_BPM = 140;  // High energy for work
  const REST_BPM = 80;   // Chill for rest
  const WATER_REMINDER_INTERVAL = 10 * 60; // 10 minutes in seconds

  // Initialize audio context
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    speechSynthRef.current = window.speechSynthesis;
    
    // Create master gain for music
    beatGainRef.current = audioContextRef.current.createGain();
    beatGainRef.current.connect(audioContextRef.current.destination);
    beatGainRef.current.gain.value = musicVolume;
    
    // Load saved hydration data
    const savedHydration = localStorage.getItem('hydrationData');
    if (savedHydration) {
      const data = JSON.parse(savedHydration);
      if (data.date === new Date().toDateString()) {
        setTotalWaterConsumed(data.intake || 0);
      }
    }
    
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (musicIntervalRef.current) clearInterval(musicIntervalRef.current);
      if (waterReminderRef.current) clearInterval(waterReminderRef.current);
      if (audioContextRef.current) audioContextRef.current.close();
      if (speechSynthRef.current) speechSynthRef.current.cancel();
    };
  }, []);

  // Water reminder timer
  useEffect(() => {
    if (isRunning && !isWaterBreak) {
      waterReminderRef.current = setInterval(() => {
        setTimeSinceLastWater(prev => {
          const newTime = prev + 1;
          // Show water reminder every 10 minutes
          if (newTime >= WATER_REMINDER_INTERVAL && newTime % WATER_REMINDER_INTERVAL === 0) {
            setShowWaterReminder(true);
            if (audioEnabled) {
              speak("Time for a water break! Stay hydrated.");
            }
          }
          return newTime;
        });
      }, 1000);
    } else {
      if (waterReminderRef.current) clearInterval(waterReminderRef.current);
    }
    
    return () => {
      if (waterReminderRef.current) clearInterval(waterReminderRef.current);
    };
  }, [isRunning, isWaterBreak, audioEnabled]);

  // Handle water break countdown
  useEffect(() => {
    if (isWaterBreak && waterBreakTimeLeft > 0) {
      const waterTimer = setInterval(() => {
        setWaterBreakTimeLeft(prev => {
          if (prev <= 1) {
            clearInterval(waterTimer);
            endWaterBreak();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(waterTimer);
    }
  }, [isWaterBreak, waterBreakTimeLeft]);

  const startWaterBreak = () => {
    setIsWaterBreak(true);
    setWaterBreakTimeLeft(30);
    setIsRunning(false);
    if (intervalRef.current) clearInterval(intervalRef.current);
    speak("Water break! Take 30 seconds to hydrate. Staying hydrated keeps you performing at your best.");
  };

  const endWaterBreak = () => {
    setIsWaterBreak(false);
    setShowWaterReminder(false);
    setTimeSinceLastWater(0);
    setIsRunning(true);
    startTimer();
    speak("Water break complete. Back to the grind! Let's go!");
  };

  const logWater = (oz) => {
    const newTotal = totalWaterConsumed + oz;
    setTotalWaterConsumed(newTotal);
    setTimeSinceLastWater(0);
    
    // Save to localStorage
    const today = new Date().toDateString();
    const data = {
      date: today,
      intake: newTotal,
      lastDrink: new Date().toISOString()
    };
    localStorage.setItem('hydrationData', JSON.stringify(data));
    
    // Voice feedback for hydration milestones
    if (newTotal >= 128 && (newTotal - oz) < 128) {
      speak("Amazing! You've hit your daily hydration goal. Great job staying hydrated.");
    } else if (newTotal >= 64 && (newTotal - oz) < 64) {
      speak("Halfway to your hydration goal. Keep it up!");
    }
    
    toast.success(`+${oz}oz water logged! Total: ${newTotal}oz`);
  };

  const skipWaterBreak = () => {
    setIsWaterBreak(false);
    setShowWaterReminder(false);
    setIsRunning(true);
    startTimer();
  };

  // Update music volume
  useEffect(() => {
    if (beatGainRef.current) {
      beatGainRef.current.gain.value = musicEnabled ? musicVolume : 0;
    }
  }, [musicVolume, musicEnabled]);

  // Play a single beat (drum-like sound)
  const playBeat = useCallback((isKick = true, accent = false) => {
    if (!musicEnabled || !audioContextRef.current || !beatGainRef.current) return;
    
    try {
      const ctx = audioContextRef.current;
      if (ctx.state === 'suspended') ctx.resume();
      
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      const filter = ctx.createBiquadFilter();
      
      osc.connect(filter);
      filter.connect(gain);
      gain.connect(beatGainRef.current);
      
      if (isKick) {
        // Kick drum sound
        osc.frequency.setValueAtTime(isResting ? 60 : 80, ctx.currentTime);
        osc.frequency.exponentialRampToValueAtTime(30, ctx.currentTime + 0.1);
        filter.type = 'lowpass';
        filter.frequency.value = 200;
        gain.gain.setValueAtTime(accent ? 1 : 0.7, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.15);
        osc.type = 'sine';
      } else {
        // Hi-hat sound
        osc.frequency.value = isResting ? 6000 : 8000;
        filter.type = 'highpass';
        filter.frequency.value = 5000;
        gain.gain.setValueAtTime(accent ? 0.15 : 0.08, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.05);
        osc.type = 'square';
      }
      
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.2);
    } catch (e) {
      // Silently fail
    }
  }, [musicEnabled, isResting]);

  // Play synth pad (background tone)
  const playSynthPad = useCallback(() => {
    if (!musicEnabled || !audioContextRef.current || !beatGainRef.current) return;
    
    try {
      const ctx = audioContextRef.current;
      if (ctx.state === 'suspended') ctx.resume();
      
      // Work mode: energetic chord (C major)
      // Rest mode: calm chord (F major)
      const frequencies = isResting 
        ? [174.61, 220, 261.63] // F3, A3, C4 - calm
        : [261.63, 329.63, 392]; // C4, E4, G4 - energetic
      
      frequencies.forEach((freq, i) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        const filter = ctx.createBiquadFilter();
        
        osc.connect(filter);
        filter.connect(gain);
        gain.connect(beatGainRef.current);
        
        osc.frequency.value = freq;
        osc.type = 'sine';
        filter.type = 'lowpass';
        filter.frequency.value = isResting ? 400 : 800;
        
        gain.gain.setValueAtTime(0, ctx.currentTime);
        gain.gain.linearRampToValueAtTime(0.08, ctx.currentTime + 0.5);
        gain.gain.linearRampToValueAtTime(0.08, ctx.currentTime + 1.5);
        gain.gain.linearRampToValueAtTime(0, ctx.currentTime + 2);
        
        osc.start(ctx.currentTime);
        osc.stop(ctx.currentTime + 2);
      });
    } catch (e) {
      // Silently fail
    }
  }, [musicEnabled, isResting]);

  // Music beat loop
  useEffect(() => {
    if (!isRunning || !musicEnabled) {
      if (musicIntervalRef.current) {
        clearInterval(musicIntervalRef.current);
        musicIntervalRef.current = null;
      }
      return;
    }

    const bpm = isResting ? REST_BPM : WORK_BPM;
    setCurrentBPM(bpm);
    const beatInterval = 60000 / bpm / 2; // Eighth notes
    let beatCount = 0;

    // Play initial synth pad
    playSynthPad();

    musicIntervalRef.current = setInterval(() => {
      beatCount++;
      
      // Kick on 1 and 3 (every 4th eighth note)
      if (beatCount % 4 === 1) {
        playBeat(true, beatCount % 8 === 1); // Accent on 1
      }
      
      // Hi-hat on every eighth note (quieter on off-beats)
      playBeat(false, beatCount % 2 === 1);
      
      // Synth pad every 8 beats
      if (beatCount % 16 === 0) {
        playSynthPad();
      }
    }, beatInterval);

    return () => {
      if (musicIntervalRef.current) {
        clearInterval(musicIntervalRef.current);
        musicIntervalRef.current = null;
      }
    };
  }, [isRunning, musicEnabled, isResting, playBeat, playSynthPad]);

  // Play beep sound
  const playBeep = useCallback((frequency = 800, duration = 150, type = 'sine') => {
    if (!audioEnabled || !audioContextRef.current) return;
    
    try {
      const ctx = audioContextRef.current;
      if (ctx.state === 'suspended') ctx.resume();
      
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);
      
      oscillator.frequency.value = frequency;
      oscillator.type = type;
      gainNode.gain.value = 0.3;
      
      oscillator.start();
      oscillator.stop(ctx.currentTime + duration / 1000);
    } catch (e) {
      console.log('Audio error:', e);
    }
  }, [audioEnabled]);

  // Speak announcement
  const speak = useCallback((text) => {
    if (!audioEnabled || !speechSynthRef.current) return;
    
    try {
      speechSynthRef.current.cancel();
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = 1.1;
      utterance.pitch = 1.0;
      utterance.volume = 0.8;
      speechSynthRef.current.speak(utterance);
    } catch (e) {
      console.log('Speech error:', e);
    }
  }, [audioEnabled]);

  // Timer logic
  useEffect(() => {
    if (!isRunning || isWaterBreak) return;

    intervalRef.current = setInterval(() => {
      setTimeLeft((prev) => {
        // Audio cues at specific times
        if (prev === 31) speak(isResting ? "30 seconds of rest remaining" : "30 seconds remaining");
        if (prev === 11) speak(isResting ? "10 seconds, get ready" : "10 seconds, push through");
        if (prev === 6) speak("5");
        if (prev === 4) playBeep(600, 100);
        if (prev === 3) playBeep(700, 100);
        if (prev === 2) playBeep(800, 100);
        if (prev === 1) playBeep(1000, 300);
        
        if (prev <= 1) {
          // Period complete
          if (isResting) {
            // Rest complete, start next set
            if (currentSet < sets) {
              setCurrentSet(s => s + 1);
              setIsResting(false);
              speak(`Set ${currentSet + 1}. Let's go!`);
              playBeep(1200, 400);
              return drill.duration_seconds;
            } else {
              // All sets complete
              setIsRunning(false);
              setCompleted(true);
              speak("Workout complete! Great job!");
              playBeep(1500, 500);
              return 0;
            }
          } else {
            // Work period complete
            if (currentSet < sets) {
              setIsResting(true);
              speak(`Set ${currentSet} complete. Rest period starting.`);
              playBeep(600, 300);
              return customRestTime; // Use custom rest time
            } else {
              // Final set complete
              setIsRunning(false);
              setCompleted(true);
              speak("Workout complete! You crushed it!");
              playBeep(1500, 500);
              return 0;
            }
          }
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(intervalRef.current);
  }, [isRunning, isResting, currentSet, sets, drill, speak, playBeep, isWaterBreak, customRestTime]);

  const startTimer = () => {
    if (audioContextRef.current?.state === 'suspended') {
      audioContextRef.current.resume();
    }
    setIsRunning(true);
    speak(`Starting ${drill.name}. Set 1 of ${sets}. Go!`);
    playBeep(1200, 400);
  };

  const pauseTimer = () => {
    setIsRunning(false);
    speak("Paused");
  };

  const resetTimer = () => {
    setIsRunning(false);
    setIsResting(false);
    setCurrentSet(1);
    setTimeLeft(drill.duration_seconds);
    setCompleted(false);
  };

  const skipToRest = () => {
    if (!isResting && currentSet <= sets) {
      setIsResting(true);
      setTimeLeft(drill.rest_seconds);
      speak("Skipping to rest");
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const totalProgress = ((currentSet - 1) / sets + (isResting ? 0.5 : 0) * (1 / sets) + 
    (1 - timeLeft / (isResting ? customRestTime : drill.duration_seconds)) * (0.5 / sets)) * 100;

  // Water Break Modal
  if (isWaterBreak) {
    return (
      <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4" data-testid="water-break-modal">
        <Card className="w-full max-w-md bg-gradient-to-br from-cyan-900/50 to-blue-900/50 border-cyan-500/30">
          <CardContent className="p-6 text-center">
            {/* Water Animation */}
            <div className="relative w-24 h-24 mx-auto mb-4">
              <div className="absolute inset-0 bg-cyan-500/20 rounded-full animate-ping" />
              <div className="absolute inset-2 bg-cyan-500/30 rounded-full animate-pulse" />
              <div className="absolute inset-0 flex items-center justify-center">
                <Droplets className="w-12 h-12 text-cyan-400" />
              </div>
            </div>

            <h2 className="text-2xl font-heading font-bold text-white mb-1">
              💧 Water Break
            </h2>
            <p className="text-cyan-300 text-sm mb-4">Stay hydrated, stay sharp!</p>

            {/* Timer */}
            <div className="mb-4">
              <div className="text-5xl font-mono font-bold text-cyan-400 mb-2">
                {waterBreakTimeLeft}s
              </div>
              <Progress value={((30 - waterBreakTimeLeft) / 30) * 100} className="h-2" />
            </div>

            {/* Log Water */}
            <div className="space-y-3 mb-4">
              <p className="text-sm text-white">Log your water intake:</p>
              <div className="flex gap-2 justify-center">
                <Button onClick={() => logWater(8)} className="bg-cyan-600 hover:bg-cyan-500">
                  +8oz
                </Button>
                <Button onClick={() => logWater(16)} variant="outline" className="border-cyan-500/30 text-cyan-400">
                  +16oz
                </Button>
                <Button onClick={() => logWater(24)} variant="outline" className="border-cyan-500/30 text-cyan-400">
                  +24oz
                </Button>
              </div>
              <p className="text-xs text-cyan-300">Today's total: {totalWaterConsumed}oz</p>
            </div>

            {/* Actions */}
            <div className="flex gap-3 justify-center">
              <Button onClick={endWaterBreak} className="bg-primary hover:bg-primary/90">
                <Play className="w-4 h-4 mr-2" />
                Resume Workout
              </Button>
              <Button onClick={skipWaterBreak} variant="outline" className="text-muted-foreground">
                Skip
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4" data-testid="workout-timer-modal">
      <Card className="w-full max-w-lg bg-gradient-to-b from-card to-background border-primary/30">
        <CardContent className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-xl font-heading font-bold text-white">{drill.name}</h2>
              <p className="text-sm text-muted-foreground">Set {currentSet} of {sets}</p>
            </div>
            <div className="flex items-center gap-1">
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={() => setMusicEnabled(!musicEnabled)}
                className={`${musicEnabled ? 'text-accent' : 'text-muted-foreground'} hover:text-white`}
                title="Toggle workout music"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 18V5l12-2v13"/>
                  <circle cx="6" cy="18" r="3"/>
                  <circle cx="18" cy="16" r="3"/>
                </svg>
              </Button>
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={() => setAudioEnabled(!audioEnabled)}
                className={`${audioEnabled ? 'text-primary' : 'text-muted-foreground'} hover:text-white`}
                title="Toggle voice cues"
              >
                {audioEnabled ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
              </Button>
            </div>
          </div>

          {/* Music Controls - Only show when music is enabled */}
          {musicEnabled && (
            <div className="mb-4 p-3 rounded-lg bg-accent/10 border border-accent/20">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full ${isRunning ? 'bg-accent animate-pulse' : 'bg-muted-foreground'}`} />
                  <span className="text-xs font-medium text-accent">
                    {isRunning ? `♫ ${currentBPM} BPM` : '♫ Music Ready'}
                  </span>
                </div>
                <span className="text-xs text-muted-foreground">
                  {isResting ? 'Chill Mode' : 'Power Mode'}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <VolumeX className="w-3 h-3 text-muted-foreground" />
                <input 
                  type="range" 
                  min="0" 
                  max="100" 
                  value={musicVolume * 100}
                  onChange={(e) => setMusicVolume(e.target.value / 100)}
                  className="flex-1 h-1 accent-accent bg-muted rounded-full appearance-none cursor-pointer"
                />
                <Volume2 className="w-3 h-3 text-muted-foreground" />
              </div>
            </div>
          )}

          {/* Timer Display */}
          <div className={`text-center py-8 rounded-lg mb-6 ${
            isResting 
              ? 'bg-blue-500/20 border border-blue-500/30' 
              : 'bg-primary/20 border border-primary/30'
          }`}>
            <div className="text-6xl md:text-8xl font-mono font-bold text-white mb-2">
              {formatTime(timeLeft)}
            </div>
            <Badge className={isResting ? 'bg-blue-500/30 text-blue-300' : 'bg-primary/30 text-primary'}>
              {completed ? 'COMPLETE' : isResting ? 'REST' : 'WORK'}
            </Badge>
            
            {/* Countdown visual for last 5 seconds */}
            {timeLeft <= 5 && timeLeft > 0 && isRunning && (
              <div className="mt-4 flex justify-center gap-2">
                {[5, 4, 3, 2, 1].map((n) => (
                  <div 
                    key={n}
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all ${
                      timeLeft >= n ? 'bg-primary text-white scale-110' : 'bg-muted/30 text-muted-foreground'
                    }`}
                  >
                    {n}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Progress */}
          <div className="mb-6">
            <div className="flex justify-between text-xs text-muted-foreground mb-1">
              <span>Overall Progress</span>
              <span>{Math.round(totalProgress)}%</span>
            </div>
            <Progress value={totalProgress} className="h-2" />
            
            {/* Set indicators */}
            <div className="flex justify-center gap-2 mt-3">
              {Array.from({ length: sets }, (_, i) => (
                <div
                  key={i}
                  className={`w-3 h-3 rounded-full transition-all ${
                    i + 1 < currentSet 
                      ? 'bg-green-500' 
                      : i + 1 === currentSet 
                        ? isResting ? 'bg-blue-500' : 'bg-primary animate-pulse'
                        : 'bg-muted/30'
                  }`}
                />
              ))}
            </div>
          </div>

          {/* Controls */}
          {!completed ? (
            <div className="space-y-3">
              <div className="flex gap-3 justify-center">
                {!isRunning ? (
                  <Button 
                    onClick={startTimer} 
                    size="lg" 
                    className="bg-primary hover:bg-primary/90 px-8"
                    data-testid="timer-start-btn"
                  >
                    <Play className="w-5 h-5 mr-2" />
                    {currentSet === 1 && timeLeft === drill.duration_seconds ? 'Start' : 'Resume'}
                  </Button>
                ) : (
                  <Button 
                    onClick={pauseTimer} 
                    size="lg" 
                    variant="outline"
                    className="px-8"
                    data-testid="timer-pause-btn"
                  >
                    <Pause className="w-5 h-5 mr-2" />
                    Pause
                  </Button>
                )}
                
                {!isResting && isRunning && (
                  <Button 
                    onClick={skipToRest} 
                    size="lg" 
                    variant="outline"
                    className="text-blue-400 border-blue-400/30"
                  >
                    <SkipForward className="w-5 h-5 mr-2" />
                    Skip to Rest
                  </Button>
                )}
                
                <Button 
                  onClick={resetTimer} 
                  size="lg" 
                  variant="ghost"
                  className="text-muted-foreground"
                >
                  <RotateCcw className="w-5 h-5" />
                </Button>
              </div>

              {/* Water Break & Rest Settings Row */}
              <div className="flex gap-2 justify-center">
                <Button
                  onClick={startWaterBreak}
                  size="sm"
                  variant="outline"
                  className="border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/20"
                  data-testid="water-break-btn"
                >
                  <Droplets className="w-4 h-4 mr-1" />
                  Water Break
                </Button>
                <Button
                  onClick={() => setShowRestSettings(!showRestSettings)}
                  size="sm"
                  variant="outline"
                  className="border-zinc-600 text-zinc-400 hover:bg-zinc-800"
                >
                  <Settings className="w-4 h-4 mr-1" />
                  Rest: {customRestTime}s
                </Button>
              </div>

              {/* Rest Time Adjuster */}
              {showRestSettings && (
                <div className="p-3 bg-zinc-800/50 rounded-lg border border-zinc-700">
                  <p className="text-xs text-muted-foreground mb-2">Adjust rest time between sets:</p>
                  <div className="flex items-center gap-3 justify-center">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setCustomRestTime(Math.max(15, customRestTime - 15))}
                    >
                      <Minus className="w-4 h-4" />
                    </Button>
                    <span className="text-lg font-bold text-white w-16 text-center">{customRestTime}s</span>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setCustomRestTime(Math.min(180, customRestTime + 15))}
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="flex gap-2 justify-center mt-2">
                    <Badge 
                      className="cursor-pointer hover:bg-blue-500/30"
                      onClick={() => setCustomRestTime(30)}
                    >30s</Badge>
                    <Badge 
                      className="cursor-pointer hover:bg-blue-500/30"
                      onClick={() => setCustomRestTime(60)}
                    >60s</Badge>
                    <Badge 
                      className="cursor-pointer hover:bg-blue-500/30"
                      onClick={() => setCustomRestTime(90)}
                    >90s</Badge>
                    <Badge 
                      className="cursor-pointer hover:bg-blue-500/30"
                      onClick={() => setCustomRestTime(120)}
                    >2m</Badge>
                  </div>
                </div>
              )}

              {/* Water Reminder Banner */}
              {showWaterReminder && !isWaterBreak && (
                <div className="p-3 bg-cyan-500/20 border border-cyan-500/30 rounded-lg animate-pulse">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Droplets className="w-5 h-5 text-cyan-400" />
                      <span className="text-cyan-300 text-sm font-medium">Time for a water break!</span>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" onClick={startWaterBreak} className="bg-cyan-600 hover:bg-cyan-500">
                        Take Break
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setShowWaterReminder(false)}>
                        Later
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* Hydration Stats */}
              <div className="flex items-center justify-center gap-4 text-xs text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Droplets className="w-3 h-3 text-cyan-400" />
                  Today: {totalWaterConsumed}oz
                </span>
                <span>|</span>
                <span>
                  Last drink: {Math.floor(timeSinceLastWater / 60)}m ago
                </span>
              </div>
            </div>
          ) : (
            <div className="text-center space-y-4">
              <div className="flex items-center justify-center gap-2 text-green-500">
                <CheckCircle className="w-6 h-6" />
                <span className="font-bold">Drill Complete!</span>
              </div>
              <div className="flex gap-3 justify-center">
                <Button onClick={resetTimer} variant="outline">
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Do Again
                </Button>
                <Button onClick={onComplete} className="bg-green-600 hover:bg-green-700">
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Mark Complete
                </Button>
              </div>
            </div>
          )}

          {/* Close button */}
          <Button 
            onClick={onClose} 
            variant="ghost" 
            className="w-full mt-4 text-muted-foreground"
          >
            Close Timer
          </Button>

          {/* Audio cue legend */}
          <div className="mt-4 pt-4 border-t border-border text-xs text-muted-foreground text-center space-y-1">
            <div>
              <Volume2 className="w-3 h-3 inline mr-1" />
              Voice cues at 30s, 10s, and countdown
            </div>
            <div>
              <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="inline mr-1">
                <path d="M9 18V5l12-2v13"/>
                <circle cx="6" cy="18" r="3"/>
                <circle cx="18" cy="16" r="3"/>
              </svg>
              Music syncs to workout: {WORK_BPM} BPM (work) → {REST_BPM} BPM (rest)
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Phase information imported from GrindConstants
// Skill levels imported from GrindConstants

const GrindPage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [progress, setProgress] = useState(null);
  const [todaysDrills, setTodaysDrills] = useState([]);
  const [completedDays, setCompletedDays] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [activeDrill, setActiveDrill] = useState(null);
  const [dailyQuote, setDailyQuote] = useState(null);
  const [showQuoteShare, setShowQuoteShare] = useState(false);
  const [showMilestoneShare, setShowMilestoneShare] = useState(false);
  const [milestoneStreak, setMilestoneStreak] = useState(0);
  const [dailyChallenge, setDailyChallenge] = useState(null);
  const [showLevelUp, setShowLevelUp] = useState(false);
  const [beastModeUnlocked, setBeastModeUnlocked] = useState(false);
  const [beastModeActive, setBeastModeActive] = useState(false);
  const [stats, setStats] = useState({ totalReps: 0, totalMiles: 0, totalMinutes: 0, workoutsCompleted: 0 });
  const [challengeProgress, setChallengeProgress] = useState({});
  const [skillLevel, setSkillLevel] = useState(null); // beginner, intermediate, advanced
  const [showSkillSelect, setShowSkillSelect] = useState(false);
  const [exerciseSwaps, setExerciseSwaps] = useState({}); // User's exercise swap preferences
  
  // Elite Beast Mode state
  const [isEliteUser, setIsEliteUser] = useState(false);
  const [weeklyEliteChallenge, setWeeklyEliteChallenge] = useState(null);
  const [eliteChallengeProgress, setEliteChallengeProgress] = useState({});
  const [activeEliteChallenge, setActiveEliteChallenge] = useState(null);
  
  // AI Drill Analyzer state
  const [showDrillAnalyzer, setShowDrillAnalyzer] = useState(false);
  const [analyzerDrillContext, setAnalyzerDrillContext] = useState("");
  
  // Undo/Redo state for completed days
  const [completedHistory, setCompletedHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user'));
    setUser(userData);
    fetchProgress();
    fetchTodaysDrills();
    
    // Load saved skill level
    const savedSkillLevel = localStorage.getItem('grindSkillLevel');
    if (savedSkillLevel) setSkillLevel(savedSkillLevel);
    
    // Set daily quote based on day of year
    const dayOfYear = Math.floor((new Date() - new Date(new Date().getFullYear(), 0, 0)) / 86400000);
    setDailyQuote(MOTIVATIONAL_QUOTES[dayOfYear % MOTIVATIONAL_QUOTES.length]);
    
    // Set daily challenge
    setDailyChallenge(DAILY_CHALLENGES[dayOfYear % DAILY_CHALLENGES.length]);
    
    // Load saved challenge progress
    const savedProgress = localStorage.getItem('challengeProgress');
    if (savedProgress) setChallengeProgress(JSON.parse(savedProgress));
    
    // Load Elite challenge progress
    const savedEliteProgress = localStorage.getItem('eliteChallengeProgress');
    if (savedEliteProgress) setEliteChallengeProgress(JSON.parse(savedEliteProgress));
    
    // Check subscription status for Elite features
    fetchSubscriptionStatus();
    
    // Set weekly Elite challenge
    setWeeklyEliteChallenge(getWeeklyEliteChallenge());
    
    // Fetch exercise preferences
    fetchExercisePreferences();
  }, []);

  const fetchSubscriptionStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/subscription/my-subscription`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Check if user has Elite subscription
      const isElite = response.data.tier_id === 'elite' && response.data.is_active;
      setIsEliteUser(isElite);
    } catch (error) {
      console.log('Subscription check failed, defaulting to non-elite');
      setIsEliteUser(false);
    }
  };

  const fetchExercisePreferences = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/exercise-preferences`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setExerciseSwaps(response.data.swaps || {});
    } catch (error) {
      console.log('No exercise preferences found');
    }
  };

  const handleExerciseSwap = (original, alternative) => {
    setExerciseSwaps(prev => {
      if (alternative === null) {
        const newSwaps = { ...prev };
        delete newSwaps[original];
        return newSwaps;
      }
      return { ...prev, [original]: alternative };
    });
  };

  // Undo/Redo/Reset functions for Grind progress
  const addToHistory = (newCompletedDays) => {
    const newHistory = completedHistory.slice(0, historyIndex + 1);
    newHistory.push([...newCompletedDays]);
    setCompletedHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
  };

  const handleUndo = async () => {
    if (historyIndex > 0) {
      const previousState = completedHistory[historyIndex - 1];
      setHistoryIndex(historyIndex - 1);
      setCompletedDays(new Set(previousState));
      
      // Sync with backend
      try {
        const token = localStorage.getItem('token');
        await axios.post(`${API}/grind/sync-progress`, {
          completed_days: previousState
        }, { headers: { Authorization: `Bearer ${token}` }});
        toast.success('Undid last action');
      } catch (error) {
        toast.error('Failed to undo');
      }
    }
  };

  const handleRedo = async () => {
    if (historyIndex < completedHistory.length - 1) {
      const nextState = completedHistory[historyIndex + 1];
      setHistoryIndex(historyIndex + 1);
      setCompletedDays(new Set(nextState));
      
      // Sync with backend
      try {
        const token = localStorage.getItem('token');
        await axios.post(`${API}/grind/sync-progress`, {
          completed_days: nextState
        }, { headers: { Authorization: `Bearer ${token}` }});
        toast.success('Redid action');
      } catch (error) {
        toast.error('Failed to redo');
      }
    }
  };

  const handleResetProgress = async () => {
    if (!window.confirm('Are you sure you want to reset all progress? This cannot be undone.')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/users/reset_progress`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Reset local state
      setCompletedDays(new Set());
      setProgress(null);
      setCompletedHistory([[]]);
      setHistoryIndex(0);
      localStorage.removeItem('grindSkillLevel');
      setSkillLevel(null);
      
      toast.success('Progress reset successfully');
      fetchProgress();
    } catch (error) {
      toast.error('Failed to reset progress');
    }
  };

  const canUndo = historyIndex > 0;
  const canRedo = historyIndex < completedHistory.length - 1;

  const fetchProgress = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/grind/progress`, { headers });
      setProgress(response.data);
      const days = response.data.completed_days || [];
      setCompletedDays(new Set(days));
      
      // Initialize history if empty
      if (completedHistory.length === 0) {
        setCompletedHistory([days]);
        setHistoryIndex(0);
      }
      
      // Load skill level for calculations
      const savedSkillLevel = localStorage.getItem('grindSkillLevel') || 'beginner';
      const skillConfig = SKILL_LEVELS[savedSkillLevel];
      
      // Calculate stats from completed days
      const completedCount = response.data.completed_days?.length || 0;
      const estimatedReps = completedCount * 150; // ~150 reps per workout avg
      const estimatedMiles = completedCount * 0.5; // ~0.5 miles per workout
      const estimatedMinutes = completedCount * 45; // ~45 min per workout
      
      setStats({
        totalReps: estimatedReps,
        totalMiles: estimatedMiles.toFixed(1),
        totalMinutes: estimatedMinutes,
        workoutsCompleted: completedCount
      });
      
      // Check Beast Mode unlock conditions (based on total days for skill level)
      const streak = response.data.streak || 0;
      const totalDays = skillConfig?.totalDays || 161;
      const progressPercent = (completedCount / totalDays) * 100;
      if (streak >= 7 || progressPercent >= 25) {
        setBeastModeUnlocked(true);
      }
      
      // Show level-up animation if hitting milestones
      const isMilestone = [7, 14, 28, 56, 84, 112].includes(completedCount);
      if (isMilestone) {
        setShowLevelUp(true);
        setTimeout(() => setShowLevelUp(false), 3000);
      }

      // Streak share prompt — only once per milestone per user
      const STREAK_MILESTONES = [7, 14, 30, 50, 100];
      if (STREAK_MILESTONES.includes(streak)) {
        const seenKey = `share_prompt_streak_${streak}`;
        if (!localStorage.getItem(seenKey)) {
          localStorage.setItem(seenKey, "1");
          setMilestoneStreak(streak);
          setShowMilestoneShare(true);
        }
      }
    } catch (error) {
      console.error('Error fetching progress:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTodaysDrills = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/grind/today`, { headers });
      setTodaysDrills(response.data.drills || []);
    } catch (error) {
      console.error('Error fetching today\'s drills:', error);
    }
  };

  const startGrind = async (selectedSkillLevel = 'beginner') => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const skillConfig = SKILL_LEVELS[selectedSkillLevel];
      
      // Save skill level
      localStorage.setItem('grindSkillLevel', selectedSkillLevel);
      setSkillLevel(selectedSkillLevel);
      
      // Start grind with skill level info
      await axios.post(`${API}/grind/start`, { 
        skill_level: selectedSkillLevel,
        start_week: skillConfig.startWeek 
      }, { headers });
      
      const totalDays = skillConfig.totalDays;
      const message = selectedSkillLevel === 'advanced' 
        ? `🔥 Advanced Break the Cycle started at Week 5! Let's dominate!`
        : `💪 Break the Cycle and Grind started! Let's get to work!`;
      
      toast.success(message);
      setShowSkillSelect(false);
      fetchProgress();
      fetchTodaysDrills();
    } catch (error) {
      toast.error('Failed to start grind');
    }
  };

  const selectSkillAndStart = (level) => {
    startGrind(level);
  };

  const completeChallenge = (challengeId) => {
    const challenge = DAILY_CHALLENGES.find(c => c.id === challengeId);
    if (challenge && !challengeProgress[challengeId]) {
      const newProgress = { ...challengeProgress, [challengeId]: true };
      setChallengeProgress(newProgress);
      localStorage.setItem('challengeProgress', JSON.stringify(newProgress));
      toast.success(`🎉 Challenge Complete! +${challenge.points} points`);
    }
  };

  const toggleBeastMode = () => {
    if (beastModeUnlocked) {
      setBeastModeActive(!beastModeActive);
      if (!beastModeActive) {
        toast.success('🔥 BEAST MODE ACTIVATED! +20% intensity on all exercises!');
      } else {
        toast.info('Beast Mode deactivated');
      }
    }
  };

  // Elite Beast Mode Challenge Functions
  const activateEliteChallenge = (challenge) => {
    if (!isEliteUser) {
      toast.error('Elite subscription required for Beast Mode Challenges!');
      navigate('/subscription');
      return;
    }
    setActiveEliteChallenge(challenge);
    toast.success(`🔥 ${challenge.name} ACTIVATED! ${challenge.description}`);
  };

  const completeEliteChallenge = (challengeId) => {
    const challenge = ELITE_BEAST_CHALLENGES.find(c => c.id === challengeId);
    if (challenge && !eliteChallengeProgress[challengeId]) {
      const newProgress = { ...eliteChallengeProgress, [challengeId]: { 
        completed: true, 
        completedAt: new Date().toISOString() 
      }};
      setEliteChallengeProgress(newProgress);
      localStorage.setItem('eliteChallengeProgress', JSON.stringify(newProgress));
      setActiveEliteChallenge(null);
      toast.success(`🏆 ELITE CHALLENGE COMPLETE! +${challenge.points} points earned!`);
    }
  };

  const cancelEliteChallenge = () => {
    setActiveEliteChallenge(null);
    toast.info('Elite challenge cancelled');
  };

  const getDifficultyColor = (difficulty) => {
    switch(difficulty) {
      case 'MEDIUM': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      case 'HARD': return 'bg-orange-500/20 text-orange-400 border-orange-500/30';
      case 'EXTREME': return 'bg-red-500/20 text-red-400 border-red-500/30';
      case 'LEGENDARY': return 'bg-purple-500/20 text-purple-400 border-purple-500/30';
      default: return 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30';
    }
  };

  const getUserLevel = () => {
    const completed = progress?.completed_days?.length || 0;
    if (completed >= 140) return { level: 7, name: "Legend", icon: Crown, color: "text-yellow-400" };
    if (completed >= 112) return { level: 6, name: "Champion", icon: Trophy, color: "text-yellow-500" };
    if (completed >= 84) return { level: 5, name: "Elite", icon: Crown, color: "text-purple-500" };
    if (completed >= 56) return { level: 4, name: "Veteran", icon: Shield, color: "text-blue-500" };
    if (completed >= 28) return { level: 3, name: "Warrior", icon: Award, color: "text-green-500" };
    if (completed >= 14) return { level: 2, name: "Competitor", icon: TrendingUp, color: "text-teal-500" };
    return { level: 1, name: "Rookie", icon: Star, color: "text-gray-400" };
  };

  const getAdaptiveDifficulty = () => {
    const completionRate = progress?.completed_days?.length ? 
      (progress.completed_days.length / progress.current_day) * 100 : 100;
    
    if (completionRate >= 90) return { level: "Intense", multiplier: 1.2, color: "text-red-500" };
    if (completionRate >= 70) return { level: "Standard", multiplier: 1.0, color: "text-yellow-500" };
    return { level: "Recovery", multiplier: 0.8, color: "text-green-500" };
  };

  const getCurrentPhase = (day) => {
    const week = Math.ceil(day / 7);
    for (const phase of PHASES) {
      if (week >= phase.week[0] && week <= phase.week[1]) {
        return phase;
      }
    }
    return PHASES[0];
  };

  const getPhaseColor = (phase) => {
    switch (phase.color) {
      case 'blue': return 'from-blue-500/20 to-blue-500/5 border-blue-500/30';
      case 'purple': return 'from-purple-500/20 to-purple-500/5 border-purple-500/30';
      case 'orange': return 'from-orange-500/20 to-orange-500/5 border-orange-500/30';
      case 'green': return 'from-green-500/20 to-green-500/5 border-green-500/30';
      default: return 'from-primary/20 to-primary/5 border-primary/30';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-[#FF4500] text-xl font-heading uppercase">Loading your grind...</div>
      </div>
    );
  }

  const currentPhase = progress?.current_day ? getCurrentPhase(progress.current_day) : PHASES[0];
  const currentSkillConfig = SKILL_LEVELS[skillLevel || 'beginner'];
  const totalDays = currentSkillConfig?.totalDays || 161;
  const progressPercent = progress?.current_day ? ((progress.current_day - 1) / totalDays) * 100 : 0;
  const userLevel = getUserLevel();
  const difficulty = getAdaptiveDifficulty();

  return (
    <div className={`min-h-screen bg-[#050505] p-4 md:p-8 relative ${beastModeActive ? 'beast-mode' : ''}`}>
      <div className="noise-overlay" />
      {/* Level Up Animation */}
      {showLevelUp && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 animate-pulse">
          <div className="text-center">
            <Sparkles className="w-24 h-24 mx-auto text-[#CCFF00] animate-bounce" />
            <h2 className="text-5xl font-heading text-[#CCFF00] mt-4">LEVEL UP!</h2>
            <p className="text-white text-xl mt-2">You've reached {userLevel.name}!</p>
          </div>
        </div>
      )}

      <div className="max-w-6xl mx-auto space-y-8">
        {/* Personalized Header */}
        <div className="text-center relative">
          {beastModeActive && (
            <div className="absolute inset-0 bg-gradient-to-r from-red-500/20 via-orange-500/20 to-red-500/20 animate-pulse" />
          )}
          <div className="flex items-center justify-center gap-3 mb-3">
            <userLevel.icon className={`w-5 h-5 ${userLevel.color}`} />
            <Badge className={`${userLevel.color} bg-black/50 rounded-none font-heading`}>{userLevel.name}</Badge>
            {skillLevel && (
              <Badge className="bg-[#CCFF00]/10 text-[#CCFF00] rounded-none font-mono text-[10px]">{SKILL_LEVELS[skillLevel]?.label}</Badge>
            )}
          </div>
          <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#FF4500] mb-2">
            {totalDays} Days to Transform
          </p>
          <h1 className="text-5xl md:text-7xl font-heading text-white tracking-tight uppercase leading-none">
            {user?.name ? `${user.name.split(' ')[0]}'s` : ''} GRIND
          </h1>
          
          {/* Daily Quote */}
          {dailyQuote && (
            <div className="mt-6 p-4 bg-white/[0.02] border border-white/[0.06] max-w-2xl mx-auto" data-testid="daily-quote-card">
              <p className="text-white/70 italic text-sm">"{dailyQuote.quote}"</p>
              <div className="flex items-center justify-between mt-2 gap-3">
                <p className="text-[#FF4500] text-xs font-mono">— {dailyQuote.author}</p>
                <button
                  onClick={() => setShowQuoteShare(true)}
                  data-testid="share-quote-btn"
                  className="text-[10px] font-mono uppercase tracking-[0.2em] text-white/40 hover:text-[#FF4500] transition-colors flex items-center gap-1.5"
                >
                  <Share2 className="w-3 h-3" /> Share Card
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Not Started State - Skill Level Selection */}
        {!progress?.started && !showSkillSelect && (
          <Card className="bg-gradient-to-br from-primary/20 to-accent/20 border-primary/50">
            <CardContent className="p-8 text-center">
              <Flame className="w-16 h-16 mx-auto text-primary mb-4" />
              <h2 className="text-2xl font-heading font-bold text-white mb-2">
                Ready to Transform, {user?.name?.split(' ')[0] || 'Athlete'}?
              </h2>
              <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                Choose your starting level. The full program is 23 weeks. 
                Advanced athletes skip the foundation phase and start at Week 5.
              </p>
              <Button
                onClick={() => setShowSkillSelect(true)}
                size="lg"
                className="bg-primary hover:bg-primary/90 text-lg px-8"
                data-testid="start-grind-btn"
              >
                <Flame className="w-5 h-5 mr-2" />
                Choose Your Level
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Skill Level Selection Modal */}
        {!progress?.started && showSkillSelect && (
          <Card className="bg-gradient-to-br from-card to-background border-primary/30">
            <CardContent className="p-8">
              <h2 className="text-2xl font-heading font-bold text-white text-center mb-6">
                Select Your Training Level
              </h2>
              <div className="grid md:grid-cols-3 gap-4">
                {/* Beginner */}
                <Card 
                  className="bg-blue-500/10 border-blue-500/30 hover:border-blue-500/60 cursor-pointer transition-all hover:scale-105"
                  onClick={() => selectSkillAndStart('beginner')}
                  data-testid="skill-beginner"
                >
                  <CardContent className="p-6 text-center">
                    <Target className="w-12 h-12 mx-auto text-blue-500 mb-3" />
                    <h3 className="text-xl font-bold text-white mb-2">Beginner</h3>
                    <p className="text-sm text-muted-foreground mb-4">New to structured training</p>
                    <div className="space-y-1 text-sm">
                      <p className="text-blue-400">📅 23 weeks (161 days)</p>
                      <p className="text-blue-400">🏃 Start at Week 1</p>
                      <p className="text-blue-400">💪 Full foundation phase</p>
                    </div>
                  </CardContent>
                </Card>

                {/* Intermediate */}
                <Card 
                  className="bg-purple-500/10 border-purple-500/30 hover:border-purple-500/60 cursor-pointer transition-all hover:scale-105"
                  onClick={() => selectSkillAndStart('intermediate')}
                  data-testid="skill-intermediate"
                >
                  <CardContent className="p-6 text-center">
                    <Dumbbell className="w-12 h-12 mx-auto text-purple-500 mb-3" />
                    <h3 className="text-xl font-bold text-white mb-2">Intermediate</h3>
                    <p className="text-sm text-muted-foreground mb-4">Some training experience</p>
                    <div className="space-y-1 text-sm">
                      <p className="text-purple-400">📅 23 weeks (161 days)</p>
                      <p className="text-purple-400">🏃 Start at Week 1</p>
                      <p className="text-purple-400">⚡ Higher intensity</p>
                    </div>
                  </CardContent>
                </Card>

                {/* Advanced */}
                <Card 
                  className="bg-gradient-to-br from-red-500/20 to-orange-500/20 border-red-500/30 hover:border-red-500/60 cursor-pointer transition-all hover:scale-105 ring-2 ring-red-500/30"
                  onClick={() => selectSkillAndStart('advanced')}
                  data-testid="skill-advanced"
                >
                  <CardContent className="p-6 text-center">
                    <Flame className="w-12 h-12 mx-auto text-red-500 mb-3 animate-pulse" />
                    <h3 className="text-xl font-bold text-white mb-2">Advanced</h3>
                    <Badge className="bg-red-500/20 text-red-400 mb-2">Recommended for Athletes</Badge>
                    <p className="text-sm text-muted-foreground mb-4">Experienced athlete</p>
                    <div className="space-y-1 text-sm">
                      <p className="text-red-400">📅 19 weeks (133 days)</p>
                      <p className="text-red-400">🚀 Start at Week 5</p>
                      <p className="text-red-400">🔥 Skip foundation, max intensity</p>
                    </div>
                  </CardContent>
                </Card>
              </div>
              <p className="text-center text-muted-foreground text-sm mt-6">
                All levels complete 23 weeks total. Advanced skips Weeks 1-4 (foundation) and starts at Hypertrophy phase.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Active Grind State */}
        {progress?.started && (
          <>
            {/* Stats Bar */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Card className="bg-gradient-to-br from-blue-500/20 to-blue-500/5 border-blue-500/30">
                <CardContent className="p-4 text-center">
                  <TrendingUp className="w-6 h-6 mx-auto text-blue-500 mb-1" />
                  <div className="text-2xl font-bold text-white">{stats.totalReps.toLocaleString()}</div>
                  <div className="text-xs text-muted-foreground">Total Reps</div>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-green-500/20 to-green-500/5 border-green-500/30">
                <CardContent className="p-4 text-center">
                  <Target className="w-6 h-6 mx-auto text-green-500 mb-1" />
                  <div className="text-2xl font-bold text-white">{stats.totalMiles}</div>
                  <div className="text-xs text-muted-foreground">Miles Run</div>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-purple-500/20 to-purple-500/5 border-purple-500/30">
                <CardContent className="p-4 text-center">
                  <Clock className="w-6 h-6 mx-auto text-purple-500 mb-1" />
                  <div className="text-2xl font-bold text-white">{stats.totalMinutes}</div>
                  <div className="text-xs text-muted-foreground">Minutes Trained</div>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-orange-500/20 to-orange-500/5 border-orange-500/30">
                <CardContent className="p-4 text-center">
                  <Award className="w-6 h-6 mx-auto text-orange-500 mb-1" />
                  <div className="text-2xl font-bold text-white">{stats.workoutsCompleted}</div>
                  <div className="text-xs text-muted-foreground">Workouts Done</div>
                </CardContent>
              </Card>
            </div>

            {/* Beast Mode & Difficulty */}
            <div className="flex flex-wrap gap-3 justify-center">
              {beastModeUnlocked && (
                <Button
                  onClick={toggleBeastMode}
                  className={`${beastModeActive 
                    ? 'bg-gradient-to-r from-red-600 to-orange-600 animate-pulse' 
                    : 'bg-gradient-to-r from-gray-700 to-gray-800'} border-none`}
                  data-testid="beast-mode-btn"
                >
                  <Flame className="w-5 h-5 mr-2" />
                  {beastModeActive ? '🔥 BEAST MODE ON' : 'Activate Beast Mode'}
                </Button>
              )}
              <Badge className={`${difficulty.color} bg-black/50 px-4 py-2`}>
                <Zap className="w-4 h-4 mr-1 inline" />
                {difficulty.level} Mode ({Math.round(difficulty.multiplier * 100)}%)
              </Badge>
            </div>

            {/* Progress Overview */}
            <Card className={`bg-gradient-to-r ${getPhaseColor(currentPhase)} border ${beastModeActive ? 'ring-2 ring-red-500' : ''}`}>
              <CardContent className="p-6">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <currentPhase.icon className={`w-5 h-5 text-${currentPhase.color}-500`} />
                      <Badge className={`bg-${currentPhase.color}-500/20 text-${currentPhase.color}-400`}>
                        {currentPhase.name}
                      </Badge>
                      <userLevel.icon className={`w-4 h-4 ${userLevel.color}`} />
                    </div>
                    <h2 className="text-3xl font-heading font-black text-white">
                      Day {progress.current_day} of {totalDays}
                    </h2>
                    <p className="text-muted-foreground">{currentPhase.focus}</p>
                  </div>
                  <div className="text-right">
                    <div className="flex items-center gap-2 justify-end mb-1">
                      <Flame className={`w-5 h-5 ${progress.streak >= 7 ? 'text-red-500 animate-pulse' : 'text-orange-500'}`} />
                      <span className="text-2xl font-bold text-white">{progress.streak || 0}</span>
                      <span className="text-muted-foreground">day streak</span>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {progress.completed_count || 0} days completed
                    </p>
                  </div>
                </div>
                <div className="mt-4">
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-muted-foreground">Overall Progress</span>
                    <span className="text-white font-bold">{Math.round(progressPercent)}%</span>
                  </div>
                  <Progress value={progressPercent} className="h-3" />
                </div>
                
                {/* Action Controls - Undo, Redo, Reset */}
                <div className="mt-4 pt-4 border-t border-border/50 flex items-center justify-between">
                  <ActionControls
                    onUndo={handleUndo}
                    onRedo={handleRedo}
                    onReset={handleResetProgress}
                    canUndo={canUndo}
                    canRedo={canRedo}
                    variant="compact"
                    resetLabel="Reset all progress"
                    undoLabel="Undo last completion"
                    redoLabel="Redo completion"
                  />
                  <span className="text-xs text-muted-foreground">
                    History: {historyIndex + 1}/{completedHistory.length}
                  </span>
                </div>
              </CardContent>
            </Card>

            {/* Daily Challenge */}
            {dailyChallenge && (
              <Card className="bg-gradient-to-r from-yellow-500/20 to-orange-500/20 border-yellow-500/30">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="text-3xl">{dailyChallenge.icon}</span>
                      <div>
                        <h3 className="font-bold text-white flex items-center gap-2">
                          Daily Challenge: {dailyChallenge.name}
                          <Badge className="bg-yellow-500/20 text-yellow-400">+{dailyChallenge.points} pts</Badge>
                        </h3>
                        <p className="text-sm text-muted-foreground">{dailyChallenge.description}</p>
                      </div>
                    </div>
                    <Button
                      onClick={() => completeChallenge(dailyChallenge.id)}
                      disabled={challengeProgress[dailyChallenge.id]}
                      className={challengeProgress[dailyChallenge.id] 
                        ? 'bg-green-600' 
                        : 'bg-yellow-600 hover:bg-yellow-700'}
                    >
                      {challengeProgress[dailyChallenge.id] ? (
                        <><CheckCircle className="w-4 h-4 mr-1" /> Done</>
                      ) : (
                        'Complete'
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Team Challenge */}
            <Card className="bg-gradient-to-r from-indigo-500/20 to-purple-500/20 border-indigo-500/30">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Users className="w-5 h-5 text-indigo-400" />
                  <h3 className="font-bold text-white">Team Challenge</h3>
                  <Badge className="bg-indigo-500/20 text-indigo-400">Active</Badge>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{TEAM_CHALLENGES[0].icon}</span>
                    <div>
                      <p className="font-medium text-white">{TEAM_CHALLENGES[0].name}</p>
                      <p className="text-sm text-muted-foreground">{TEAM_CHALLENGES[0].description}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-white">
                      {Math.round((stats.totalReps / TEAM_CHALLENGES[0].target) * 100)}%
                    </div>
                    <Progress 
                      value={Math.min((stats.totalReps / TEAM_CHALLENGES[0].target) * 100, 100)} 
                      className="w-24 h-2" 
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* ELITE Beast Mode Challenges - Extracted Component */}
            <GrindBeastMode
              isEliteUser={isEliteUser}
              weeklyEliteChallenge={weeklyEliteChallenge}
              activeEliteChallenge={activeEliteChallenge}
              eliteChallengeProgress={eliteChallengeProgress}
              onActivateChallenge={activateEliteChallenge}
              onCompleteChallenge={completeEliteChallenge}
              onCancelChallenge={cancelEliteChallenge}
              getDifficultyColor={getDifficultyColor}
            />

            {/* Today's Workout */}
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="text-xl font-heading text-white flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-primary" />
                  Today's Workout
                </CardTitle>
              </CardHeader>
              <CardContent>
                {todaysDrills.length > 0 ? (
                  <div className="space-y-4">
                    {todaysDrills.map((drill, index) => (
                      <DrillWithExample 
                        key={drill.id} 
                        drill={drill} 
                        index={index}
                        onStart={() => navigate('/engine', { state: { selectedDrill: drill } })}
                        onStartTimer={() => setActiveDrill(drill)}
                        currentSwap={exerciseSwaps[drill.name]}
                        onSwap={handleExerciseSwap}
                      />
                    ))}
                    <div className="flex gap-3">
                      <Button
                        onClick={() => setActiveDrill(todaysDrills[0])}
                        className="flex-1 bg-accent hover:bg-accent/90"
                        data-testid="start-timed-workout"
                      >
                        <Timer className="w-5 h-5 mr-2" />
                        Start with Timer
                      </Button>
                      <Button
                        onClick={() => navigate('/engine', { state: { selectedDrill: todaysDrills[0] } })}
                        variant="outline"
                        className="flex-1"
                        data-testid="start-today-workout"
                      >
                        <Play className="w-5 h-5 mr-2" />
                        Open in Engine
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Moon className="w-12 h-12 mx-auto text-purple-500 mb-3" />
                    <p className="text-muted-foreground">Rest day - recover and come back stronger!</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Day Tracker & Phase Breakdown - Extracted Component */}
            <GrindDayTracker
              progress={progress}
              completedDays={completedDays}
              skillLevel={skillLevel}
            />
          </>
        )}
      </div>

      {/* Workout Timer Modal */}
      {activeDrill && (
        <WorkoutTimer 
          drill={activeDrill}
          sets={activeDrill.sets || 3}
          onComplete={async () => {
            toast.success(`${activeDrill.name} complete!`);
            setActiveDrill(null);
            // Could also mark progress here
          }}
          onClose={() => setActiveDrill(null)}
        />
      )}

      {/* Floating AI Analyze Button */}
      <FloatingAnalyzeButton onClick={() => {
        setAnalyzerDrillContext(activeDrill?.name || "");
        setShowDrillAnalyzer(true);
      }} />

      {/* AI Drill Analyzer Modal */}
      <DrillAnalyzer
        isOpen={showDrillAnalyzer}
        onClose={() => setShowDrillAnalyzer(false)}
        drillContext={analyzerDrillContext}
      />

      {/* Quote of the Day Share Modal */}
      <QuoteShareModal
        open={showQuoteShare}
        onClose={() => setShowQuoteShare(false)}
        quote={dailyQuote?.quote}
      />

      {/* Streak Milestone Share Prompt */}
      <QuoteShareModal
        open={showMilestoneShare}
        onClose={() => setShowMilestoneShare(false)}
        quote={dailyQuote?.quote}
        milestoneStreak={milestoneStreak}
      />
    </div>
  );
};

// Drill component with visual example and exercise swapper
const DrillWithExample = ({ drill, index, onStart, onStartTimer, currentSwap, onSwap }) => {
  const [showExample, setShowExample] = useState(false);
  
  // Get display name (use swap if exists)
  const displayName = currentSwap || drill.name;
  
  // Exercise examples with step-by-step visuals
  const getExampleContent = (drillName) => {
    const examples = {
      default: {
        steps: drill.instructions || [],
        tips: [
          "Focus on form over speed",
          "Breathe steadily throughout",
          "Rest fully between sets",
          "Stay hydrated"
        ],
        muscleGroups: drill.muscle_groups || [],
        benefit: drill.basketball_benefit || "Improves overall conditioning"
      }
    };
    return examples.default;
  };

  const example = getExampleContent(drill.name);

  return (
    <div className="border border-border rounded-sm overflow-hidden">
      {/* Drill Header */}
      <div 
        className="p-4 bg-background hover:bg-muted/20 cursor-pointer transition-colors"
        onClick={() => setShowExample(!showExample)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-primary/20 text-primary flex items-center justify-center font-bold">
              {index + 1}
            </div>
            <div>
              <h4 className="font-bold text-white flex items-center gap-2">
                {displayName}
                {currentSwap && currentSwap !== drill.name && (
                  <Badge className="bg-accent/20 text-accent text-xs">
                    <ArrowLeftRight className="w-3 h-3 mr-1" />
                    Swapped
                  </Badge>
                )}
              </h4>
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <span>{Math.floor(drill.duration_seconds / 60)}:{(drill.duration_seconds % 60).toString().padStart(2, '0')}</span>
                <span>•</span>
                <span>{drill.sets || 3} sets</span>
                <span>•</span>
                <Badge variant="outline" className="text-xs">{drill.difficulty || 'intermediate'}</Badge>
                {drill.alternatives && drill.alternatives.length > 0 && (
                  <>
                    <span>•</span>
                    <Badge variant="outline" className="text-xs text-green-400 border-green-400/30">
                      {drill.alternatives.length} alternatives
                    </Badge>
                  </>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); setShowExample(!showExample); }}>
              {showExample ? 'Hide' : 'Show'} Example
            </Button>
            {onStartTimer && (
              <Button 
                size="sm" 
                variant="outline"
                className="text-accent border-accent/30"
                onClick={(e) => { e.stopPropagation(); onStartTimer(); }}
                data-testid={`timer-btn-${index}`}
              >
                <Timer className="w-4 h-4" />
              </Button>
            )}
            <Button size="sm" onClick={(e) => { e.stopPropagation(); onStart(); }}>
              <Play className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Exercise Swapper - Shows alternatives if available */}
        {drill.alternatives && drill.alternatives.length > 0 && (
          <div onClick={(e) => e.stopPropagation()}>
            <ExerciseSwapper 
              drill={drill} 
              currentSwap={currentSwap}
              onSwap={onSwap}
            />
          </div>
        )}
      </div>

      {/* Example Content */}
      {showExample && (
        <div className="p-4 bg-muted/10 border-t border-border space-y-4">
          {/* Video Demonstration removed — manga step-by-step is primary form reference */}

          {/* Description */}
          <div>
            <p className="text-sm text-muted-foreground">{drill.description}</p>
          </div>

          {/* Step-by-step Instructions */}
          <div>
            <h5 className="text-sm font-bold text-white mb-2 flex items-center gap-2">
              <Target className="w-4 h-4 text-primary" />
              How to Perform
            </h5>
            <ol className="space-y-2">
              {example.steps.map((step, i) => (
                <li key={i} className="flex gap-3 text-sm">
                  <span className="w-6 h-6 rounded-full bg-primary/20 text-primary flex items-center justify-center flex-shrink-0 text-xs font-bold">
                    {i + 1}
                  </span>
                  <span className="text-muted-foreground">{step}</span>
                </li>
              ))}
            </ol>
          </div>

          {/* Visual Cues Grid */}
          <div className="grid grid-cols-2 gap-3">
            {/* Muscles Worked */}
            <div className="p-3 bg-background rounded-sm">
              <h6 className="text-xs font-bold text-white mb-2 flex items-center gap-1">
                <Dumbbell className="w-3 h-3 text-orange-500" />
                Muscles Worked
              </h6>
              <div className="flex flex-wrap gap-1">
                {(example.muscleGroups.length > 0 ? example.muscleGroups : ['Full body']).map((muscle, i) => (
                  <Badge key={i} variant="outline" className="text-xs capitalize">
                    {muscle}
                  </Badge>
                ))}
              </div>
            </div>

            {/* Basketball Benefit */}
            <div className="p-3 bg-background rounded-sm">
              <h6 className="text-xs font-bold text-white mb-2 flex items-center gap-1">
                <Zap className="w-3 h-3 text-yellow-500" />
                Game Benefit
              </h6>
              <p className="text-xs text-muted-foreground">{example.benefit}</p>
            </div>
          </div>

          {/* Pro Tips */}
          <div className="p-3 bg-primary/5 rounded-sm border border-primary/20">
            <h6 className="text-xs font-bold text-primary mb-2 flex items-center gap-1">
              <Star className="w-3 h-3" />
              Pro Tips
            </h6>
            <ul className="space-y-1">
              {example.tips.map((tip, i) => (
                <li key={i} className="text-xs text-muted-foreground flex items-start gap-2">
                  <CheckCircle className="w-3 h-3 text-primary mt-0.5 flex-shrink-0" />
                  {tip}
                </li>
              ))}
            </ul>
          </div>

          {/* Frequency Recommendation */}
          {drill.frequency_per_week && (
            <div className="flex items-center gap-2 text-xs">
              <Calendar className="w-4 h-4 text-accent" />
              <span className="text-accent font-medium">
                Recommended: {drill.frequency_per_week}x per week
              </span>
            </div>
          )}

          {/* Common Mistakes — surfaces the cues that separate trash form from elite */}
          {drill.common_mistakes && drill.common_mistakes.length > 0 && (
            <div className="p-3 bg-red-500/5 rounded-sm border border-red-500/20" data-testid="drill-common-mistakes">
              <h6 className="text-xs font-bold text-red-400 mb-2 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                Common Mistakes
              </h6>
              <ul className="space-y-1">
                {drill.common_mistakes.map((m, i) => (
                  <li key={i} className="text-xs text-muted-foreground flex items-start gap-2">
                    <span className="text-red-400 mt-0.5 flex-shrink-0">✕</span>
                    {m}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Progression — beginner → intermediate → advanced scaling */}
          {drill.progression && (
            <div className="p-3 bg-emerald-500/5 rounded-sm border border-emerald-500/20" data-testid="drill-progression">
              <h6 className="text-xs font-bold text-emerald-400 mb-2 flex items-center gap-1">
                <TrendingUp className="w-3 h-3" />
                How to Progress
              </h6>
              <p className="text-xs text-muted-foreground leading-relaxed">{drill.progression}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default GrindPage;
